import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useServerFn } from '@tanstack/react-start'
import { AuthCard, AuthForm } from '@/components/auth'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { signUpFn } from '@/server/functions/auth'

const signUpSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
})

type SignUpFormData = z.infer<typeof signUpSchema>

export const Route = createFileRoute('/_auth/sign-up')({
  component: SignUpPage,
})

function SignUpPage() {
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const signUp = useServerFn(signUpFn)

  const form = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const onSubmit = async (data: SignUpFormData) => {
    setIsLoading(true)
    try {
      await signUp({ data })
      navigate({ to: '/dashboard' })
    } catch (error: unknown) {
      const err = error as { message?: string }
      form.setError('root', {
        message: err?.message || 'Failed to create account',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthCard
      title="Sign Up"
      description="Create your account to start shortening links"
    >
      <AuthForm
        schema={signUpSchema}
        defaultValues={{ email: '', password: '' }}
        onSubmit={onSubmit}
        submitText="CREATE ACCOUNT"
        loadingText="CREATING..."
        isLoading={isLoading}
        form={form}
      >
        {(form) => (
          <>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#8E9196] font-mono text-sm">
                    EMAIL
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      placeholder="user@example.com"
                      className="bg-[#0a0a0a] border-[#9B87F5]/20 text-white font-mono focus:border-[#9B87F5] placeholder:text-[#8E9196]/50"
                    />
                  </FormControl>
                  <FormMessage className="text-red-400 font-mono text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#8E9196] font-mono text-sm">
                    PASSWORD
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="password"
                      placeholder="••••••••"
                      className="bg-[#0a0a0a] border-[#9B87F5]/20 text-white font-mono focus:border-[#9B87F5] placeholder:text-[#8E9196]/50"
                    />
                  </FormControl>
                  <FormMessage className="text-red-400 font-mono text-xs" />
                </FormItem>
              )}
            />
          </>
        )}
      </AuthForm>

      <div className="mt-6 text-center">
        <p className="text-[#8E9196] font-mono text-sm">
          // Already have an account?{' '}
          <Link
            to="/sign-in"
            className="text-[#9B87F5] hover:text-white transition-colors"
          >
            SIGN_IN
          </Link>
        </p>
      </div>
    </AuthCard>
  )
}
